import React, { useState, useEffect } from 'react';
import StoryDownloader from './StoryDownloader';
import ConsoleOverlay from './ConsoleOverlay';
import { Search, Plus, Trash2, Heart, Loader, History, Clock, X } from 'lucide-react';
import './App.css';
import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableStoryItem = ({ user, isSuper, isLive, isRecording, isNew, onOpen, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: user.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2000 : 1,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`story-item ${isDragging ? 'dragging' : ''}`}>
      <div className="avatar-wrapper">
        <div className={`story-ring ${user.stories?.length === 0 ? 'empty-ring' : ''} ${isSuper ? 'super-ring' : ''} ${isLive ? 'live-ring' : ''}`} {...attributes} {...listeners}>
          <img src={user.avatar || 'https://via.placeholder.com/150'} alt={user.username} className="story-avatar-preview" onClick={() => onOpen(user)} />
          {isNew && <div className="new-story-dot"></div>}
          {isSuper ? (
            <div className="super-fav-badge" onClick={(e) => {
              e.stopPropagation();
              onToggle(user.id);
            }} title="Rétrograder en favori normal">
              <Heart size={10} color="#fff" fill="#fff" />
            </div>
          ) : (
            <button className="upgrade-to-super" onClick={(e) => {
              e.stopPropagation();
              onToggle(user.id);
            }} title="Passer en Super Favori (++)">
              <Plus size={10} color="#fff" />
            </button>
          )}
        </div>
      </div>
      <span className="story-username-preview">{user.username}</span>
      <div className="status-pills">
        {isLive && <div className="live-pill">LIVE</div>}
        {isRecording && <div className="rec-pill">REC</div>}
      </div>
    </div>
  );
};

function App() {
  const [isAuthorized, setIsAuthorized] = useState(() => {
    return sessionStorage.getItem('instasto_auth') === 'true';
  });
  const [accessCode, setAccessCode] = useState('');
  const [authError, setAuthError] = useState(false);

  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('instasto_favorites');
    if (saved) {
      try { 
        const parsed = JSON.parse(saved); 
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) { return []; }
    }
    return [];
  });

  const handleAuth = (e) => {
    e.preventDefault();
    if (accessCode === '6555') {
      setIsAuthorized(true);
      sessionStorage.setItem('instasto_auth', 'true');
    } else {
      setAuthError(true);
      setAccessCode('');
      setTimeout(() => setAuthError(false), 2000);
    }
  };

  const [platform, setPlatform] = useState('instagram'); // 'instagram' | 'snapchat'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showRefreshPrompt, setShowRefreshPrompt] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState({ current: 0, total: 0, username: '' });
  const [viewerState, setViewerState] = useState({ isOpen: false, selectedUser: null });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [superFavorites, setSuperFavorites] = useState(() => {
    const saved = localStorage.getItem('instasto_super_favorites');
    if (saved) {
      try { 
        const parsed = JSON.parse(saved); 
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) { return []; }
    }
    return [];
  });

  const [liveUserIds, setLiveUserIds] = useState([]);
  const [recordingUserIds, setRecordingUserIds] = useState([]);
  const [latestStoryTimes, setLatestStoryTimes] = useState({});
  const [seenStories, setSeenStories] = useState(() => {
    const saved = localStorage.getItem('instasto_seen_stories');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { return {}; }
    }
    return {};
  });
  const [liveHistory, setLiveHistory] = useState([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isConsoleOpen, setIsConsoleOpen] = useState(false);
  const [konamiIdx, setKonamiIdx] = useState(0);
  const sequence = ['ArrowUp', 'ArrowDown', 'ArrowLeft'];

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Éviter de déclencher si on tape dans un input (search ou auth)
      if (e.target.tagName === 'INPUT') return;

      if (e.key === sequence[konamiIdx]) {
        if (konamiIdx === sequence.length - 1) {
          setIsConsoleOpen(true);
          setKonamiIdx(0);
        } else {
          setKonamiIdx(konamiIdx + 1);
        }
      } else {
        setKonamiIdx(0);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [konamiIdx]);

  const syncFavoritesWithBackend = async () => {
    if (!isAuthorized) return;
    try {
      const watchlist = favorites
        .filter(f => (f.platform || 'instagram') === 'instagram')
        .map(f => ({ id: f.id, username: f.username }));
      await fetch('/api/sync-favorites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(watchlist)
      });
    } catch (e) {
      console.error("Sync error:", e);
    }
  };

  const [lastRefreshTimes, setLastRefreshTimes] = useState(() => {
    const saved = localStorage.getItem('instasto_refresh_times');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { return {}; }
    }
    return {};
  });

  useEffect(() => {
    localStorage.setItem('instasto_favorites', JSON.stringify(favorites));
    localStorage.setItem('instasto_super_favorites', JSON.stringify(superFavorites));
    localStorage.setItem('instasto_seen_stories', JSON.stringify(seenStories));
    localStorage.setItem('instasto_refresh_times', JSON.stringify(lastRefreshTimes));
    
    // Synchronise avec le serveur pour le scan autonome
    syncFavoritesWithBackend();
  }, [favorites, superFavorites, seenStories, lastRefreshTimes]);

  const refreshAllFavoritesIfNeeded = async () => {
    if (!isAuthorized || favorites.length === 0) return;
    
    const now = Date.now();
    const COOLDOWN = 30 * 60 * 1000; // 30 minutes
    
    // On identifie ceux qui ont besoin d'un rafraîchissement
    const accountsToRefresh = favorites.filter(user => {
        const lastRef = lastRefreshTimes[user.id] || 0;
        return (now - lastRef) > COOLDOWN;
    });

    if (accountsToRefresh.length === 0) return;

    setIsRefreshing(true);
    setRefreshStatus({ current: 0, total: accountsToRefresh.length, username: '' });
    console.log(`📡 [Auto-Refresh] Mise à jour de ${accountsToRefresh.length} comptes...`);
    
    try {
        const newFavorites = [...favorites];
        const newRefreshTimes = { ...lastRefreshTimes };
        const newLatestTimes = { ...latestStoryTimes };

        for (let i = 0; i < accountsToRefresh.length; i++) {
            const user = accountsToRefresh[i];
            setRefreshStatus({ current: i + 1, total: accountsToRefresh.length, username: user.username });
            
            try {
                const updatedUser = await loadUserStories(user);
                const idx = newFavorites.findIndex(f => f.id === user.id);
                if (idx !== -1) {
                    newFavorites[idx] = updatedUser;
                    newRefreshTimes[user.id] = now;
                    
                    // Calcul de la story la plus récente pour le point bleu
                    if (updatedUser.stories && updatedUser.stories.length > 0) {
                        const latest = Math.max(...updatedUser.stories.map(s => s.taken_at || 0));
                        newLatestTimes[user.id] = latest;
                    }
                }
                // Petit délai pour la politesse
                await new Promise(r => setTimeout(r, 400));
            } catch (e) { console.error(`Erreur refresh ${user.username}`, e); }
        }

        setFavorites(newFavorites);
        setLastRefreshTimes(newRefreshTimes);
        setLatestStoryTimes(newLatestTimes);
    } finally {
        setIsRefreshing(false);
    }
  };

  const fetchLiveStatus = async () => {
    if (!isAuthorized) return;
    try {
      const resp = await fetch('/api/live-status');
      const data = await resp.json();
      if (data.live_ids) setLiveUserIds(data.live_ids);
      if (data.recording_ids) setRecordingUserIds(data.recording_ids);
      if (data.latest_story_times) setLatestStoryTimes(data.latest_story_times);
    } catch (e) {
      console.error("Live poll error:", e);
    }
  };

  const fetchLiveHistory = async () => {
    try {
      const resp = await fetch('/api/live-history');
      const data = await resp.json();
      setLiveHistory(data);
    } catch (e) {
      console.error("History fetch error:", e);
    }
  };

  const checkAndPromptRefresh = () => {
    if (!isAuthorized || favorites.length === 0) return;
    const now = Date.now();
    const COOLDOWN = 30 * 60 * 1000;
    const needsRefresh = favorites.some(user => (now - (lastRefreshTimes[user.id] || 0)) > COOLDOWN);
    if (needsRefresh) setShowRefreshPrompt(true);
  };

  const handleSkipRefresh = () => {
    const now = Date.now();
    const newRefreshTimes = { ...lastRefreshTimes };
    favorites.forEach(f => { if (!newRefreshTimes[f.id]) newRefreshTimes[f.id] = now; });
    setLastRefreshTimes(newRefreshTimes);
    setShowRefreshPrompt(false);
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchLiveStatus();
      checkAndPromptRefresh();
      const interval = setInterval(fetchLiveStatus, 120000);
      return () => clearInterval(interval);
    }
  }, [isAuthorized, favorites.length]);

  useEffect(() => {
    if (isHistoryOpen) {
      fetchLiveHistory();
    }
  }, [isHistoryOpen]);

  const formatHistoryDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' - ' + date.toLocaleDateString();
  };

  if (!isAuthorized) {
    return (
      <div className="auth-container fade-in">
        <div className="auth-card glass-effect">
          <h1 className="logo">InstaSto <Heart size={24} color="var(--accent-color)" fill="var(--accent-color)" /></h1>
          <p>Accès sécurisé. Veuillez entrer le code.</p>
          <form onSubmit={handleAuth}>
            <input 
              type="password" 
              className={`auth-input ${authError ? 'shake' : ''}`}
              placeholder="Code d'accès"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              autoFocus
            />
            <button type="submit" className="auth-btn">Entrer</button>
          </form>
          {authError && <span className="auth-error">Code incorrect</span>}
        </div>
      </div>
    );
  }

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    try {
      const endpoint = platform === 'snapchat' ? `/api/snapchat/search?query=${encodeURIComponent(searchQuery)}` : `/api/search?query=${encodeURIComponent(searchQuery)}`;
      const resp = await fetch(endpoint);
      const data = await resp.json();
      if (Array.isArray(data)) {
        setSearchResults(data);
      }
    } catch (err) {
      console.error("Erreur de recherche", err);
    }
    setIsLoading(false);
  };

  const handleOpenDownloader = (user) => {
    // Marquer les stories comme vues
    if (latestStoryTimes[user.id]) {
        setSeenStories(prev => ({
            ...prev,
            [user.id]: latestStoryTimes[user.id]
        }));
    }
    setViewerState({ isOpen: true, selectedUser: user });
  };

  const handleCloseDownloader = () => {
    setViewerState({ isOpen: false, selectedUser: null });
  };

  const loadUserStories = async (userParam) => {
    try {
      const platformPath = userParam.platform === 'snapchat' ? 'snapchat/' : '';
      const [storiesResp, infoResp] = await Promise.all([
        fetch(`/api/${platformPath}stories/${userParam.id}`),
        fetch(`/api/${platformPath}user-info/${userParam.id}`)
      ]);
      const stories = await storiesResp.json();
      const info = await infoResp.json();
      
      return { 
        ...userParam, 
        stories: stories || [],
        avatar: info.avatar || userParam.avatar
      };
    } catch (e) {
      console.error("Erreur lors du chargement des stories ou infos user", e);
      return { ...userParam, stories: [] };
    }
  };

  const handleToggleFavorite = async (user) => {
    const isFav = favorites.find(f => f.id === user.id);
    if (isFav) {
      setFavorites(favorites.filter(f => f.id !== user.id));
      setSuperFavorites(superFavorites.filter(id => id !== user.id));
    } else {
      const userWithStories = await loadUserStories(user);
      setFavorites([...favorites, userWithStories]);
    }
  };

  const handleToggleSuperFavorite = (userId) => {
    if (superFavorites.includes(userId)) {
      setSuperFavorites(superFavorites.filter(id => id !== userId));
    } else {
      setSuperFavorites([...superFavorites, userId]);
    }
  };

  const handleDragEndSuper = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSuperFavorites((items) => {
        const activePlatformItems = items.filter(id => {
            const u = favorites.find(f => f.id === id);
            return u && (u.platform || 'instagram') === platform;
        });
        const otherPlatformItems = items.filter(id => {
            const u = favorites.find(f => f.id === id);
            return !u || (u.platform || 'instagram') !== platform;
        });

        const oldIndex = activePlatformItems.indexOf(active.id);
        const newIndex = activePlatformItems.indexOf(over.id);
        const newActive = arrayMove(activePlatformItems, oldIndex, newIndex);
        return [...newActive, ...otherPlatformItems];
      });
    }
  };

  const handleDragEndNormal = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const activePlatformFavs = favorites.filter(u => (u.platform || 'instagram') === platform);
      const otherPlatformFavs = favorites.filter(u => (u.platform || 'instagram') !== platform);

      const normalFavIds = activePlatformFavs.filter(u => !superFavorites.includes(u.id)).map(u => u.id);
      const oldIndex = normalFavIds.indexOf(active.id);
      const newIndex = normalFavIds.indexOf(over.id);
      
      const newIds = arrayMove(normalFavIds, oldIndex, newIndex);
      
      const superFavs = activePlatformFavs.filter(u => superFavorites.includes(u.id));
      const normalFavs = activePlatformFavs.filter(u => !superFavorites.includes(u.id));
      
      const orderedNormal = newIds.map(id => normalFavs.find(u => u.id === id));
      setFavorites([...superFavs, ...orderedNormal, ...otherPlatformFavs]);
    }
  };

  const currentPlatformFavorites = Array.isArray(favorites) ? favorites.filter(u => (u.platform || 'instagram') === platform) : [];
  const currentPlatformSuperFavIds = Array.isArray(superFavorites) ? superFavorites.filter(id => currentPlatformFavorites.some(f => f.id === id)) : [];

  return (
    <div className="app-container">
      {/* Header */}
      <header className="header glass-effect">
        <div className="header-content">
          <h1 className="logo">InstaSto <Heart size={20} color="var(--accent-color)" fill="var(--accent-color)" /></h1>
          <form className="search-bar" onSubmit={handleSearch}>
            <Search size={18} color="var(--text-secondary)" />
            <input 
              type="text" 
              placeholder="Rechercher des comptes..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {isLoading && <Loader className="spin" size={16} color="var(--text-secondary)" />}
          </form>
          <button 
            className={`history-toggle-btn ${isHistoryOpen ? 'active' : ''}`} 
            onClick={() => setIsHistoryOpen(!isHistoryOpen)}
            title="Historique des lives"
          >
            <History size={20} />
          </button>
        </div>
      </header>

      {/* Platform Switcher */}
      {!viewerState.isOpen && (
        <div className="platform-switcher">
          <button 
            className={`platform-btn ${platform === 'instagram' ? 'active' : ''}`}
            onClick={() => setPlatform('instagram')}
          >
            Instagram
          </button>
          <button 
            className={`platform-btn ${platform === 'snapchat' ? 'active' : ''}`}
            onClick={() => setPlatform('snapchat')}
          >
            Snapchat
          </button>
        </div>
      )}

      {!viewerState.isOpen && (
        <main className="main-content">
          
          {/* Super Favorites Section */}
          {currentPlatformSuperFavIds.length > 0 && (
            <section className="stories-section super-fav-section">
              <h2 className="section-title"><Heart size={18} fill="var(--accent-color)" color="var(--accent-color)" /> Super Favoris (++)</h2>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndSuper}>
                <SortableContext items={currentPlatformSuperFavIds} strategy={horizontalListSortingStrategy}>
                  <div className="stories-carousel">
                    {currentPlatformSuperFavIds.map((id) => {
                      const user = currentPlatformFavorites.find(u => u.id === id);
                      if (!user) return null;
                      const isNew = (latestStoryTimes[user.id] || 0) > (seenStories[user.id] || 0);
                      return (
                        <SortableStoryItem 
                          key={user.id} 
                          user={user} 
                          isSuper={true} 
                          isLive={liveUserIds.includes(user.id)}
                          isRecording={recordingUserIds.includes(user.id)}
                          isNew={isNew}
                          onOpen={handleOpenDownloader} 
                          onToggle={handleToggleSuperFavorite} 
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </section>
          )}

          {/* Normal Favorites Section */}
          <section className="stories-section">
            <h2 className="section-title">Comptes Favoris ({currentPlatformFavorites.length - currentPlatformSuperFavIds.length})</h2>
            {currentPlatformFavorites.filter(u => !currentPlatformSuperFavIds.includes(u.id)).length === 0 ? (
              currentPlatformSuperFavIds.length === 0 && (
                <div className="empty-state">
                  <p>Vous n'avez pas encore de favoris. Recherchez et ajoutez-en !</p>
                </div>
              )
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndNormal}>
                <SortableContext items={currentPlatformFavorites.filter(u => !currentPlatformSuperFavIds.includes(u.id)).map(u => u.id)} strategy={horizontalListSortingStrategy}>
                  <div className="stories-carousel">
                    {currentPlatformFavorites.filter(u => !currentPlatformSuperFavIds.includes(u.id)).map((user) => {
                      const isNew = (latestStoryTimes[user.id] || 0) > (seenStories[user.id] || 0);
                      return (
                        <SortableStoryItem 
                          key={user.id} 
                          user={user} 
                          isSuper={false} 
                          isLive={liveUserIds.includes(user.id)}
                          isRecording={recordingUserIds.includes(user.id)}
                          isNew={isNew}
                          onOpen={handleOpenDownloader} 
                          onToggle={handleToggleSuperFavorite} 
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </section>

          {/* Search Results / All Accounts */}
          <section className="explore-section">
            <h2 className="section-title">{searchResults.length > 0 ? "Résultats de recherche" : "Recherchez pour explorer"}</h2>
            <div className="accounts-grid">
              {searchResults.map((user) => {
                const isFav = currentPlatformFavorites.some(f => f.id === user.id);
                const isSuper = currentPlatformSuperFavIds.includes(user.id);
                const isLive = liveUserIds.includes(user.id);
                const isRecording = recordingUserIds.includes(user.id);
                const isNew = (latestStoryTimes[user.id] || 0) > (seenStories[user.id] || 0);
                return (
                  <div key={user.id} className="account-card glass-effect slide-up">
                    <div className="avatar-container-search">
                      <img src={user.avatar || 'https://via.placeholder.com/150'} alt={user.username} className="account-avatar" />
                      {isLive && (
                        <div className={`live-badge-search ${isRecording ? 'is-recording' : ''}`}>
                          {isRecording ? '🔴 REC' : 'LIVE'}
                        </div>
                      )}
                      {isNew && <div className="new-story-dot-search"></div>}
                    </div>
                    <div className="account-info">
                      <div className="account-title-row">
                        <h3>{user.username}</h3>
                        {isSuper && <Heart size={14} color="var(--accent-color)" fill="var(--accent-color)" />}
                      </div>
                      <p>{user.name || ''}</p>
                      {user.is_private && <span style={{fontSize: '11px', color: '#ff4d4f'}}>Privé</span>}
                    </div>
                    <div className="card-actions-group">
                      <button 
                        className={`action-btn ${isFav ? 'remove-btn' : 'add-btn'}`}
                        onClick={() => handleToggleFavorite(user)}
                        disabled={user.is_private && !isFav}
                      >
                        {isFav ? <Trash2 size={16} /> : <Plus size={16} />}
                        {isFav ? 'Retirer' : 'Ajouter'}
                      </button>
                      {isFav && (
                        <button 
                          className={`super-fav-btn ${isSuper ? 'active' : ''}`}
                          onClick={() => handleToggleSuperFavorite(user.id)}
                          title="Favori ++"
                        >
                          <Heart size={18} color={isSuper ? "#fff" : "var(--accent-color)"} fill={isSuper ? "#fff" : "transparent"} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </main>
      )}

      {/* Live History Popup */}
      {isHistoryOpen && (
        <div className="history-overlay fade-in" onClick={() => setIsHistoryOpen(false)}>
          <div className="history-modal glass-effect" onClick={e => e.stopPropagation()}>
            <div className="history-header">
              <h2><Clock size={20} /> Historique des Directs</h2>
              <button className="close-history" onClick={() => setIsHistoryOpen(false)}><X size={24} /></button>
            </div>
            <div className="history-list">
              {liveHistory.length === 0 ? (
                <div className="empty-history">Aucun live enregistré pour le moment.</div>
              ) : (
                liveHistory.map((item, index) => (
                  <div key={index} className="history-item slide-up">
                    <img src={`/api/proxy?url=${encodeURIComponent(item.avatar)}`} alt={item.username} className="history-avatar" />
                    <div className="history-info">
                      <span className="history-username">{item.username}</span>
                      <span className="history-date">{formatHistoryDate(item.time)}</span>
                    </div>
                    <div className="history-status">
                      <div className="status-dot"></div>
                      Détecté
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="history-footer">
              Gardé en mémoire : 50 derniers lives
            </div>
          </div>
        </div>
      )}


      {/* Fullscreen Story Downloader Popup */}
      {viewerState.isOpen && viewerState.selectedUser && (
        <StoryDownloader 
          user={viewerState.selectedUser} 
          onClose={handleCloseDownloader} 
        />
      )}

      {/* Global Refresh Overlay */}
      {isRefreshing && (
        <div className="global-loader-overlay fade-in">
          <div className="global-loader-card glass-effect scale-in">
            <div className="loader-spinner"></div>
            <h3>Synchronisation...</h3>
            <div className="refresh-progress">
                <p className="refreshing-user">Scan de <strong>@{refreshStatus.username}</strong></p>
                <div className="progress-counter">
                    <span className="current">{refreshStatus.current}</span>
                    <span className="separator">/</span>
                    <span className="total">{refreshStatus.total}</span>
                </div>
            </div>
            <p className="refresh-help">Mise à jour de vos favoris en cours. Veuillez patienter.</p>
          </div>
        </div>
      )}

      {/* Refresh Confirmation Prompt */}
      {showRefreshPrompt && (
        <div className="global-loader-overlay fade-in">
          <div className="global-loader-card glass-effect scale-in">
            <div className="prompt-icon">
                <Clock size={40} color="var(--accent-light)" />
            </div>
            <h3>Mise à jour disponible</h3>
            <p>Voulez-vous rafraîchir les stories de vos favoris pour voir les nouveautés ?</p>
            <div className="prompt-actions">
                <button className="prompt-btn confirm" onClick={() => { setShowRefreshPrompt(false); refreshAllFavoritesIfNeeded(); }}>
                    Oui, rafraîchir
                </button>
                <button className="prompt-btn skip" onClick={handleSkipRefresh}>
                    Plus tard
                </button>
            </div>
          </div>
        </div>
      )}
      {/* Server Console Overlay (Secret) */}
      {isConsoleOpen && (
        <ConsoleOverlay onClose={() => setIsConsoleOpen(false)} />
      )}
    </div>
  );
}

export default App;
